import { Check } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { INTENTS, type IntentKey } from "@/lib/intentFilters";
import { cn } from "@/lib/utils";

interface IntentChooserProps {
  selected: IntentKey[];
  onToggle: (key: IntentKey) => void;
  onReset: () => void;
  /** Number of matching cards for each intent given the current selection. */
  counts: Record<string, number>;
  matchCount: number;
  /** False while only the server-rendered preview is loaded — counts are hidden then. */
  countsReady?: boolean;
}

export function IntentChooser({ selected, onToggle, onReset, counts, matchCount, countsReady = true }: IntentChooserProps) {
  const visible = countsReady
    ? INTENTS.filter((intent) => selected.includes(intent.key) || (counts[intent.key] ?? 0) > 0)
    : INTENTS;

  return (
    <section aria-labelledby="intent-heading" className="rounded-2xl border border-border bg-card p-5 sm:p-6">
      <h2 id="intent-heading" className="text-lg font-semibold text-foreground">
        Was ist dir wichtig?
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Wähle aus, worauf es dir ankommt – die Rangliste passt sich sofort an. Mehrfachauswahl möglich.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-6">
        {visible.map((intent) => {
          const active = selected.includes(intent.key);
          return (
            <button
              key={intent.key}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(intent.key)}
              className={cn(
                "group relative flex h-full flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors",
                active
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border bg-background hover:border-primary/50 hover:bg-muted/50",
              )}
            >
              {active && (
                <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-3 w-3" aria-hidden="true" />
                </span>
              )}
              <span className="pr-6 text-sm font-semibold text-foreground">{intent.label}</span>
              <span className="text-xs text-muted-foreground">{intent.description}</span>
              {/* The line is always rendered so the tile keeps its height. */}
              <span className="mt-auto pt-1 text-xs font-medium text-muted-foreground">
                {countsReady ? `${counts[intent.key] ?? 0} Karten` : "\u00a0"}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="font-semibold text-foreground">
          {selected.length > 0 ? `${matchCount} Karten passen` : `${matchCount} Karten im Vergleich`}
        </span>
        {selected.length > 0 && (
          <button type="button" onClick={onReset} className="text-primary underline underline-offset-4">
            Auswahl zurücksetzen
          </button>
        )}
        <Link to="/tools/kreditkarten-finder" className="text-primary underline underline-offset-4">
          Lieber Schritt für Schritt? Zum Kartenfinder
        </Link>
      </div>

      {selected.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Passende Themenseiten:{" "}
          {selected
            .map((key) => INTENTS.find((i) => i.key === key))
            .filter((intent): intent is (typeof INTENTS)[number] => !!intent?.href)
            .map((intent, index, arr) => (
              <span key={intent.key}>
                <Link to={intent.href!} className="text-primary underline underline-offset-4">
                  {intent.label}
                </Link>
                {index < arr.length - 1 ? ", " : ""}
              </span>
            ))}
        </p>
      )}
    </section>
  );
}
